import { DotcEnums } from 'src/common/contracts/enums/dotc'
import useMakeOffer from 'src/common/contracts/hooks/dotc/useMakeOffer/useMakeOffer'
import { usePriceFeeds } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import { getPriceFeedForToken } from 'src/common/queries/usePriceFeeds/utils'
import { getTokenAddresses } from 'src/common/utils/collections/getTokenAddresses'
import {
  buyTokenAmountAtom,
  buyTokenAtom,
  limitOrderOptionsAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
} from 'src/pages/TradePage/stores/trade-store'
import { useAtomValue } from 'jotai'
import useApproveToken from 'src/common/contracts/hooks/token/useApproveToken'
import useConfig from 'src/config/hooks/useConfig'
import { useCallback, useMemo } from 'react'

const useLimitOrderTx = () => {
  const config = useConfig()
  const { makeStaticOffer, makeDynamicOffer } = useMakeOffer()
  const approveToken = useApproveToken()

  const sellToken = useAtomValue(sellTokenAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const sellTokenAmount = useAtomValue(sellTokenAmountAtom)
  const buyTokenAmount = useAtomValue(buyTokenAmountAtom)
  const limitOrderOptions = useAtomValue(limitOrderOptionsAtom)

  const tokensForPriceFeeds = limitOrderOptions.dynamic
    ? getTokenAddresses([sellToken, buyToken])
    : []
  const { priceFeeds, isLoading: priceFeedsLoading } =
    usePriceFeeds(tokensForPriceFeeds)

  const authorizationAddresses = useMemo(() => {
    if (limitOrderOptions.otherAuthorizationAddress) {
      return [
        ...limitOrderOptions.authorizationAddresses,
        limitOrderOptions.otherAuthorizationAddress,
      ]
    }

    return limitOrderOptions.authorizationAddresses
  }, [limitOrderOptions])

  const makeOfferTx = useCallback(() => {
    const approveTokenTx = approveToken({
      spender: config.contractsAddresses.dotcContractAddress,
      tokenAddress: sellToken?.contractAddress,
      tokenType: sellToken?.type,
      amount: sellTokenAmount,
    })
    let makeTx: ReturnType<typeof makeDynamicOffer>
    if (limitOrderOptions.dynamic && priceFeeds) {
      const sellTokenPriceFeed = getPriceFeedForToken(
        priceFeeds,
        sellToken?.contractAddress
      )?.price_feed_address
      const buyTokenPriceFeed = getPriceFeedForToken(
        priceFeeds,
        buyToken?.contractAddress
      )?.price_feed_address

      if (sellToken && buyToken && sellTokenPriceFeed && buyTokenPriceFeed) {
        makeTx = makeDynamicOffer({
          depositAsset: {
            address: sellToken.contractAddress,
            tokenId: sellToken.tokenId || 0,
            decimals: sellToken.decimals || 0,
            type: DotcEnums.AssetType[sellToken.type],
            amount: sellTokenAmount,
          },
          withdrawalAsset: {
            address: buyToken.contractAddress,
            tokenId: buyToken.tokenId || 0,
            decimals: buyToken.decimals || 0,
            type: DotcEnums.AssetType[buyToken.type],
          },
          depositAssetPrice: { priceFeedAddress: sellTokenPriceFeed },
          withdrawalAssetPrice: { priceFeedAddress: buyTokenPriceFeed },
          offerPrice: {
            percentage: limitOrderOptions.percentage || 0,
            percentageType:
              limitOrderOptions.percentageType === null
                ? DotcEnums.PercentageType.Plus
                : limitOrderOptions.percentageType,
          },
          takingOfferType: limitOrderOptions.takingOfferType,
          offerOptions: limitOrderOptions,
        })
      }
    } else if (sellToken && buyToken) {
      makeTx = makeStaticOffer({
        depositAsset: {
          address: sellToken.contractAddress,
          tokenId: sellToken.tokenId,
          decimals: sellToken.decimals,
          type: DotcEnums.AssetType[sellToken.type],
          amount: sellTokenAmount,
        },
        withdrawalAsset: {
          address: buyToken.contractAddress,
          tokenId: buyToken.tokenId || 0,
          decimals: buyToken.decimals,
          type: DotcEnums.AssetType[buyToken.type],
          amount: buyTokenAmount,
        },
        takingOfferType: limitOrderOptions.takingOfferType,
        offerOptions: {
          ...limitOrderOptions,
          authorizationAddresses,
        },
      })
    }
    return [approveTokenTx!, makeTx!]
  }, [
    approveToken,
    authorizationAddresses,
    buyToken,
    buyTokenAmount,
    config.contractsAddresses.dotcContractAddress,
    limitOrderOptions,
    makeDynamicOffer,
    makeStaticOffer,
    priceFeeds,
    sellToken,
    sellTokenAmount,
  ])

  return { makeOfferTx, priceFeedsLoading }
}

export default useLimitOrderTx
